import {
  Cartesian3,
  Cartographic,
  Color,
  CustomDataSource,
  EllipsoidGeodesic,
  HeightReference,
  LabelStyle,
  PolygonHierarchy,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  VerticalOrigin,
  Cartesian2,
  defined,
  sampleTerrainMostDetailed,
  type Viewer,
} from 'cesium';
import { EnuFrame } from '../geo/enuFrame';
import { store, type MeasureMode } from '../app/store';

/**
 * Measurement tools driven by a mode state machine over a single
 * ScreenSpaceEventHandler. Results live in a dedicated CustomDataSource so they
 * layer cleanly above terrain and track and can be cleared independently.
 *
 * Distance is terrain-clamped: the clicked endpoints are geodesically densified
 * and re-sampled against the most-detailed terrain, so the reported length
 * follows the ground profile rather than the straight chord.
 *
 * Height reports the DEM height difference between two picked points (plus the
 * horizontal and slope distances), drawn as a right triangle. Area is the
 * planar (E,N) shoelace area of N clicked points, computed in a local ENU frame
 * anchored at the polygon centroid; right-click closes the ring.
 */
export class MeasurementManager {
  private readonly handler: ScreenSpaceEventHandler;
  private readonly results: CustomDataSource;
  private points: Cartesian3[] = [];

  constructor(private readonly viewer: Viewer) {
    this.results = new CustomDataSource('measurements');
    void viewer.dataSources.add(this.results);
    this.handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

    store.subscribe((s) => this.setMode(s.measureMode));
  }

  private setMode(mode: MeasureMode): void {
    this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
    this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
    this.points = [];
    if (mode === 'distance') this.enableDistance();
    if (mode === 'height') this.enableHeight();
    if (mode === 'area') this.enableArea();
  }

  clear(): void {
    this.results.entities.removeAll();
    this.points = [];
  }

  private enableDistance(): void {
    this.handler.setInputAction((evt: ScreenSpaceEventHandler.PositionedEvent) => {
      const pos = this.pick(evt.position);
      if (!defined(pos)) return;
      this.points.push(pos);
      this.results.entities.add({
        position: pos,
        point: { pixelSize: 8, color: Color.YELLOW, heightReference: HeightReference.CLAMP_TO_GROUND },
      });
      if (this.points.length >= 2) void this.finishSegment();
    }, ScreenSpaceEventType.LEFT_CLICK);

    this.handler.setInputAction(() => this.reset(), ScreenSpaceEventType.RIGHT_CLICK);
  }

  private async finishSegment(): Promise<void> {
    const [a, b] = this.points.slice(-2);
    const length = await this.terrainDistance(a, b);
    this.results.entities.add({
      polyline: {
        positions: [a, b],
        width: 3,
        material: Color.YELLOW,
        clampToGround: true,
      },
    });
    this.results.entities.add({
      position: Cartesian3.midpoint(a, b, new Cartesian3()),
      label: {
        text: `${length.toFixed(1)} m`,
        font: '14px sans-serif',
        fillColor: Color.WHITE,
        outlineColor: Color.BLACK,
        outlineWidth: 3,
        style: LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: VerticalOrigin.BOTTOM,
        pixelOffset: new Cartesian2(0, -12),
        heightReference: HeightReference.CLAMP_TO_GROUND,
      },
    });
  }

  private enableHeight(): void {
    this.handler.setInputAction((evt: ScreenSpaceEventHandler.PositionedEvent) => {
      const pos = this.pick(evt.position);
      if (!defined(pos)) return;
      this.points.push(pos);
      this.results.entities.add({
        position: pos,
        point: { pixelSize: 8, color: Color.CYAN, heightReference: HeightReference.CLAMP_TO_GROUND },
      });
      if (this.points.length >= 2) void this.finishHeight();
    }, ScreenSpaceEventType.LEFT_CLICK);

    this.handler.setInputAction(() => this.reset(), ScreenSpaceEventType.RIGHT_CLICK);
  }

  private async finishHeight(): Promise<void> {
    const [a, b] = this.points.slice(-2);
    const ca = Cartographic.fromCartesian(a);
    const cb = Cartographic.fromCartesian(b);
    await sampleTerrainMostDetailed(this.viewer.terrainProvider, [ca, cb]);

    const deltaH = cb.height - ca.height;
    const horizontal = new EllipsoidGeodesic(ca, cb).surfaceDistance;
    const slope = Math.hypot(horizontal, deltaH);

    // Right triangle: slope hypotenuse a→b plus a vertical riser under the
    // higher point. Lines float in air, so show through terrain when occluded.
    const ga = Cartographic.toCartesian(ca);
    const gb = Cartographic.toCartesian(cb);
    const lowerCarto = deltaH >= 0 ? ca : cb;
    const upperCarto = deltaH >= 0 ? cb : ca;
    const upper = deltaH >= 0 ? gb : ga;
    const corner = Cartographic.toCartesian(
      new Cartographic(upperCarto.longitude, upperCarto.latitude, lowerCarto.height),
    );
    const depthFail = Color.CYAN.withAlpha(0.3);
    this.results.entities.add({
      polyline: { positions: [ga, gb], width: 3, material: Color.CYAN, depthFailMaterial: depthFail },
    });
    this.results.entities.add({
      polyline: { positions: [corner, upper], width: 3, material: Color.CYAN, depthFailMaterial: depthFail },
    });
    this.results.entities.add({
      position: Cartesian3.midpoint(corner, upper, new Cartesian3()),
      label: {
        text: `Δh ${deltaH >= 0 ? '+' : ''}${deltaH.toFixed(1)} m\n↔ ${horizontal.toFixed(1)} m · ⤢ ${slope.toFixed(1)} m`,
        font: '14px sans-serif',
        fillColor: Color.WHITE,
        outlineColor: Color.BLACK,
        outlineWidth: 3,
        style: LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: VerticalOrigin.BOTTOM,
        pixelOffset: new Cartesian2(0, -12),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
  }

  private enableArea(): void {
    this.handler.setInputAction((evt: ScreenSpaceEventHandler.PositionedEvent) => {
      const pos = this.pick(evt.position);
      if (!defined(pos)) return;
      this.points.push(pos);
      this.results.entities.add({
        position: pos,
        point: { pixelSize: 8, color: Color.ORANGE, heightReference: HeightReference.CLAMP_TO_GROUND },
      });
    }, ScreenSpaceEventType.LEFT_CLICK);

    // Right-click closes the ring (needs >= 3 vertices) or clears a false start.
    this.handler.setInputAction(() => {
      if (this.points.length >= 3) this.finishArea();
      else this.reset();
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  private finishArea(): void {
    const ring = this.points.slice();
    this.points = [];

    // Planar shoelace in a local ENU frame anchored at the vertex mean; heights
    // are irrelevant to the projected (E,N) area.
    const anchor = ring.reduce(
      (acc, p) => Cartesian3.add(acc, p, acc),
      new Cartesian3(),
    );
    Cartesian3.divideByScalar(anchor, ring.length, anchor);
    const frame = new EnuFrame(anchor);
    const local = ring.map((p) => frame.fixedToLocal(p));

    let twice = 0;
    for (let i = 0; i < local.length; i++) {
      const p = local[i];
      const q = local[(i + 1) % local.length];
      twice += p.x * q.y - q.x * p.y;
    }
    const area = Math.abs(twice) / 2;
    const text = area >= 10_000 ? `${(area / 10_000).toFixed(2)} ha` : `${area.toFixed(1)} m²`;

    this.results.entities.add({
      polygon: {
        hierarchy: new PolygonHierarchy(ring),
        material: Color.ORANGE.withAlpha(0.35),
      },
    });
    this.results.entities.add({
      position: anchor,
      label: {
        text,
        font: '14px sans-serif',
        fillColor: Color.WHITE,
        outlineColor: Color.BLACK,
        outlineWidth: 3,
        style: LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: VerticalOrigin.BOTTOM,
        pixelOffset: new Cartesian2(0, -12),
        heightReference: HeightReference.CLAMP_TO_GROUND,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
  }

  /** Geodesically densify a→b, sample terrain, sum 3D distances. */
  private async terrainDistance(a: Cartesian3, b: Cartesian3): Promise<number> {
    const ca = Cartographic.fromCartesian(a);
    const cb = Cartographic.fromCartesian(b);
    const geo = new EllipsoidGeodesic(ca, cb);
    const total = geo.surfaceDistance;
    const steps = Math.max(2, Math.ceil(total / 5)); // ~5 m spacing

    const samples: Cartographic[] = [];
    for (let i = 0; i <= steps; i++) {
      samples.push(geo.interpolateUsingFraction(i / steps));
    }
    await sampleTerrainMostDetailed(this.viewer.terrainProvider, samples);

    let length = 0;
    for (let i = 1; i < samples.length; i++) {
      length += Cartesian3.distance(
        Cartographic.toCartesian(samples[i - 1]),
        Cartographic.toCartesian(samples[i]),
      );
    }
    return length;
  }

  private pick(windowPos: Cartesian2): Cartesian3 | undefined {
    const { scene } = this.viewer;
    const picked = scene.pickPosition(windowPos);
    if (defined(picked)) return picked;
    const ray = this.viewer.camera.getPickRay(windowPos);
    return ray ? scene.globe.pick(ray, scene) : undefined;
  }

  private reset(): void {
    this.points = [];
  }
}
